import { useState } from "react";
import type { AuthClient } from "@saas-on-sheets/auth-client";
import { LoginForm } from "../components/LoginForm.tsx";
import { Banner } from "../components/Banner.tsx";

// SSO error codes that may appear via ?error=... on the LoginPage. License /
// app-specific failures (no_license_for_app, expired, revoked) no longer
// surface here — those happen post-login on the dashboard's "no access" UI.
const ERROR_MESSAGES: Record<string, string> = {
  consent_denied: "Google login was cancelled.",
  invalid_id_token: "Login failed. Please try again.",
  code_exchange_failed: "Login failed. Please try again.",
  internal_error: "Something went wrong. Please try again.",
};

function parseError(): string | null {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("error");
  if (!code) return null;
  return ERROR_MESSAGES[code] ?? "Login failed.";
}

interface LoginPageProps {
  authClient: AuthClient;
}

export function LoginPage({ authClient }: LoginPageProps) {
  const [error, setError] = useState<string | null>(parseError());

  async function handleLogin(): Promise<void> {
    setError(null);
    try {
      const { authUrl } = await authClient.startLogin({
        returnTo: window.location.origin + "/",
      });
      window.location.href = authUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    }
  }

  return (
    <main className="login-page">
      <section className="login-card" aria-labelledby="login-title">
        <header className="login-card__brand">
          <div className="login-card__logo" aria-hidden="true">
            h
          </div>
          <h1 id="login-title" className="login-card__title">
            Welcome to hello-app
          </h1>
          <p className="login-card__subtitle">A reference app for the saas-on-sheets platform.</p>
        </header>

        {error ? <Banner kind="error">{error}</Banner> : null}

        <LoginForm onSubmit={handleLogin} />

        <p className="login-card__footer">
          By continuing, you agree to the platform&apos;s acceptable use policy.
        </p>
      </section>

      <p className="login-page__attribution">saas-on-sheets · hello-app</p>
    </main>
  );
}
